import type { DefaultTemplateRootProps, RepeaterFunctionInvoker, RepeaterRow } from 'Components/types';
import type { ArrayCallback, ArrayPredicate } from 'Core/types';
import createRepeaterComponents from 'Utils/repeater';

import Vehicle, { type VehicleData } from './vehicle';

export default class VehicleCollection {
  static empty = new VehicleCollection([]);

  private readonly _vehicles: readonly Vehicle[];

  constructor(vehicles: readonly Vehicle[]) {
    const vehiclesByKey = new Map<string, Vehicle>();

    for (const vehicle of vehicles) {
      if (vehicle?.notNull) {
        vehiclesByKey.delete(vehicle.key);
        vehiclesByKey.set(vehicle.key, vehicle);
      }
    }

    this._vehicles = [...vehiclesByKey.values()];
  }

  get serializable(): VehicleData[] {
    return this._vehicles.map((v) => v.serializable);
  }

  get size(): number {
    return this._vehicles.length;
  }

  get last(): Vehicle | undefined {
    return this._vehicles[this._vehicles.length - 1];
  }

  add(vehicle: Vehicle): VehicleCollection {
    if (!vehicle?.notNull) {
      return this;
    }

    const existingVehicle = this.find(vehicle.equals);
    return existingVehicle
      ? this.replace(existingVehicle.key, vehicle)
      : new VehicleCollection([...this._vehicles, vehicle]);
  }

  concat(value: Vehicle | VehicleCollection): VehicleCollection {
    if (value instanceof VehicleCollection) {
      return value._vehicles.reduce<VehicleCollection>((vehicles, vehicle) => vehicles.add(vehicle), this);
    }
    return this.add(value);
  }

  copy(): Vehicle[] {
    return [...this._vehicles];
  }

  includes(vehicle: Vehicle): boolean {
    return this._vehicles.some(vehicle.equals);
  }

  find(predicate: ArrayPredicate<Vehicle>): Vehicle | undefined {
    return this._vehicles.find(predicate);
  }

  findByKey(key: string | null | undefined): Vehicle | undefined {
    return this._vehicles.find((vehicle) => vehicle.key === key);
  }

  map<T>(callback: ArrayCallback<Vehicle, T>): T[] {
    return this._vehicles.map(callback);
  }

  remove(vehicle: Vehicle): VehicleCollection {
    const vehicles = this._vehicles.filter((savedVehicle) => !savedVehicle.equalsBySelection(vehicle));
    return vehicles.length === this.size ? this : new VehicleCollection(vehicles);
  }

  replace(key: string, newVehicle: Vehicle): VehicleCollection {
    const vehicleToReplace = this.findByKey(key);
    if (!vehicleToReplace || vehicleToReplace === newVehicle) {
      return this;
    }

    return new VehicleCollection(
      this._vehicles.flatMap((vehicle) =>
        vehicle === vehicleToReplace ? [newVehicle] : vehicle.key === newVehicle.key ? [] : [vehicle],
      ),
    );
  }

  reverse(): Vehicle[] {
    return this.copy().reverse();
  }

  repeaterComponents<
    Params,
    RootProps extends object = DefaultTemplateRootProps,
    RootElement extends HTMLElement = HTMLElement,
  >(
    Component: RepeaterRow<Params, Vehicle, 'vehicle', RootProps, RootElement>,
  ): RepeaterFunctionInvoker<Params, RootProps, RootElement> {
    return createRepeaterComponents(this._vehicles, 'vehicle', Component, (vehicle) => vehicle.key);
  }

  slice(start?: number, end?: number): Vehicle[] {
    return this._vehicles.slice(start, end);
  }
}
